import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, Milestone,
  UploadCloud, Settings as SettingsIcon, LogOut,
  Menu, X, ChevronsLeft, ChevronsRight, Landmark, Table2, History,
  Wallet, Scale, Sun, Moon, Command, BarChart3, BadgeDollarSign, Focus,
  PiggyBank, Receipt, Megaphone, HardHat, Users, ScrollText,
  Banknote, CalendarClock, ShieldCheck,
} from "lucide-react";
import { CashflowLockup, CashflowMark } from "@/components/Logo";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { useFocus } from "@/lib/FocusContext";
import CommandPalette from "@/components/CommandPalette";
import { TEST_IDS } from "@/testIds";

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Cashflow", icon: PiggyBank, testId: "nav-cashflow", end: true },
      { to: "/projection", label: "Projection", icon: LayoutDashboard, testId: TEST_IDS.navProjection },
      { to: "/collections", label: "Collections Model", icon: Table2, testId: TEST_IDS.navCollections },
      { to: "/sales", label: "Sales Bookings", icon: TrendingUp, testId: TEST_IDS.navSales },
      { to: "/milestones", label: "Milestones", icon: Milestone, testId: TEST_IDS.navMilestones },
      { to: "/land-sales", label: "Land Sales", icon: Landmark, testId: TEST_IDS.navLandSales },
      { to: "/planning", label: "Planning", icon: Wallet, testId: "nav-planning" },
      { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
      { to: "/sales-intelligence", label: "Sales Intelligence", icon: BadgeDollarSign, testId: "nav-sales-intelligence" },
      { to: "/actuals", label: "Actuals vs Projection", icon: Scale, testId: "nav-actuals" },
      { to: "/other-receipts", label: "Other Receipts", icon: BadgeDollarSign, testId: "nav-other-receipts" },
    ],
  },
  {
    label: "Outflows",
    items: [
      { to: "/outflows/development", label: "Land Cost", icon: Landmark, testId: "nav-outflows-development" },
      { to: "/outflows/marketing", label: "Marketing Expenses", icon: Megaphone, testId: "nav-outflows-marketing" },
      { to: "/outflows/epc", label: "Construction (EPC)", icon: HardHat, testId: "nav-outflows-epc" },
      { to: "/outflows/hr", label: "Salaries & HR", icon: Users, testId: "nav-outflows-hr" },
      { to: "/outflows/statutory", label: "Statutory & Corpus", icon: ScrollText, testId: "nav-outflows-statutory" },
      { to: "/outflows/committed-lands", label: "Committed New Lands", icon: Landmark, testId: "nav-outflows-committed-lands" },
    ],
  },
  {
    label: "Loans",
    items: [
      { to: "/loans/facilities", label: "Loan Facilities", icon: Banknote, testId: "nav-loans-facilities" },
      { to: "/loans/schedule", label: "Debt Schedule", icon: CalendarClock, testId: "nav-loans-schedule" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/import", label: "Excel Import", icon: UploadCloud, testId: TEST_IDS.navImport },
      { to: "/import-history", label: "Import History", icon: History, testId: "nav-import-history" },
      { to: "/settings", label: "Settings", icon: SettingsIcon, testId: TEST_IDS.navSettings },
      { to: "/users", label: "User Roles & Access", icon: ShieldCheck, testId: "nav-users", adminOnly: true },
    ],
  },
];

function NavItem({ item, collapsed, onClick, mobile = false }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={mobile ? `${item.testId}-mobile` : item.testId}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `cf-touch relative flex items-center gap-3 transition-all duration-150 ${
          collapsed ? "justify-center px-2 py-2.5 mx-1 rounded-xl" : "px-3 py-2.5 rounded-xl"
        } ${
          isActive
            ? "text-white font-semibold"
            : "text-white/55 hover:text-white hover:bg-surface/[0.04]"
        }`
      }
      style={({ isActive }) =>
        isActive
          ? {
              background: "rgba(244, 63, 94, 0.12)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
            }
          : undefined
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
              style={{ background: "var(--cf-accent)" }}
            />
          )}
          <Icon
            size={16}
            strokeWidth={1.9}
            className="shrink-0"
            style={isActive ? { color: "var(--cf-accent-2)" } : undefined}
          />
          {!collapsed && <span className="text-[13px]">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

function SidebarBody({ user, onLogout, onNavigate, collapsed, mobile = false, theme, onToggleTheme, focus, onToggleFocus }) {
  return (
    <>
      {/* Logo block */}
      <div className={`${collapsed ? "px-3" : "px-5"} pt-5 pb-4`}>
        <Link to="/" onClick={onNavigate} className="block">
          {collapsed ? (
            <CashflowMark size={30} variant="light" />
          ) : (
            <CashflowLockup size={26} variant="light" />
          )}
        </Link>
        {!collapsed && (
          <div className="text-[10px] uppercase tracking-[0.20em] text-white/40 mt-2 font-bold">
            Radiance Realty · Internal
          </div>
        )}
      </div>

      {/* Nav sections */}
      <nav className={`flex-1 overflow-y-auto py-1 ${collapsed ? "px-1" : "px-3"} space-y-5`}>
        {NAV_SECTIONS.map((sec, idx) => {
          const items = sec.items.filter((it) => !it.adminOnly || user?.role === "admin");
          if (items.length === 0) return null;
          return (
          <div key={sec.label ?? `sec-${idx}`}>
            {!collapsed && sec.label && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-bold mb-2 px-3">
                {sec.label}
              </div>
            )}
            <div className={collapsed ? "space-y-1" : "space-y-0.5"}>
              {items.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  onClick={onNavigate}
                  mobile={mobile}
                />
              ))}
            </div>
          </div>
          );
        })}
      </nav>

      {/* Account footer */}
      <div className={`${collapsed ? "px-2" : "px-4"} pb-4 pt-3`}>
        {/* Focus + Theme toggles */}
        {!collapsed && (
          <div className="space-y-2 mb-3">
            <div
              className="flex items-center justify-between px-3 py-2 rounded-lg"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="flex items-center gap-2 text-[12px] text-white/70">
                <Focus size={13} strokeWidth={2} />
                <span>Focus mode</span>
              </div>
              <button
                data-testid={mobile ? "focus-toggle-mobile" : "focus-toggle"}
                onClick={onToggleFocus}
                aria-label="Toggle focus mode"
                className="cf-theme-toggle"
                style={{
                  background: focus ? "rgba(244,63,94,0.28)" : "rgba(255,255,255,0.14)",
                  borderColor: "rgba(255,255,255,0.14)",
                  transform: focus ? "none" : undefined,
                }}
              >
                <span
                  style={{
                    position: "absolute", top: 2,
                    left: focus ? 22 : 2,
                    width: 18, height: 18, borderRadius: 999,
                    background: "#FFFFFF",
                    transition: "left 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.20)",
                  }}
                />
              </button>
            </div>
            <div
              className="flex items-center justify-between px-3 py-2 rounded-lg"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="flex items-center gap-2 text-[12px] text-white/70">
                {theme === "dark" ? <Moon size={13} strokeWidth={2} /> : <Sun size={13} strokeWidth={2} />}
                <span>{theme === "dark" ? "Dark" : "Light"} mode</span>
              </div>
              <button
                data-testid={mobile ? "theme-toggle-mobile" : "theme-toggle"}
                onClick={onToggleTheme}
                aria-label="Toggle theme"
                className="cf-theme-toggle"
                style={{
                  background: theme === "dark" ? "rgba(244,63,94,0.28)" : "rgba(255,255,255,0.14)",
                  borderColor: "rgba(255,255,255,0.14)",
                }}
              />
            </div>
          </div>
        )}

        <div
          className={`${collapsed ? "p-2" : "p-3"} rounded-xl`}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className={`flex items-center gap-2.5 ${collapsed ? "justify-center" : "mb-2.5"}`}>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 text-white"
              style={{
                background: "linear-gradient(135deg, rgba(244,63,94,0.30) 0%, rgba(99,102,241,0.30) 100%)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{user?.name || "Admin"}</div>
                <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              data-testid={mobile ? `${TEST_IDS.navLogout}-mobile` : TEST_IDS.navLogout}
              onClick={onLogout}
              className="cf-touch w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-white/70 hover:text-white rounded-lg transition-colors"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <LogOut size={13} strokeWidth={2} />
              Sign out
            </button>
          )}
        </div>
        {collapsed && (
          <>
            <button
              data-testid={mobile ? "theme-toggle-collapsed-mobile" : "theme-toggle-collapsed"}
              onClick={onToggleTheme}
              title="Toggle theme"
              className="cf-touch mt-2 w-full flex items-center justify-center py-2 text-white/60 hover:text-white rounded-lg"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              {theme === "dark" ? <Sun size={14} strokeWidth={2} /> : <Moon size={14} strokeWidth={2} />}
            </button>
            <button
              data-testid={mobile ? `${TEST_IDS.navLogout}-mobile` : TEST_IDS.navLogout}
              onClick={onLogout}
              title="Sign out"
              className="cf-touch mt-2 w-full flex items-center justify-center py-2 text-white/60 hover:text-white rounded-lg"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <LogOut size={14} strokeWidth={2} />
            </button>
          </>
        )}
      </div>
    </>
  );
}

export default function SidebarLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const { focus, toggleFocus } = useFocus();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cf_sidebar_collapsed") === "1");

  useEffect(() => {
    localStorage.setItem("cf_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openCmdK = () => {
    // Dispatch a synthetic ⌘K keydown so CommandPalette handler opens it
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
  };

  const sidebarStyle = {
    background:
      "linear-gradient(180deg, #0A0C13 0%, #10131C 55%, #0A0C13 100%)",
    boxShadow: "inset -1px 0 0 rgba(255,255,255,0.04)",
  };

  return (
    <div className="min-h-screen flex" style={{ background: "transparent" }}>
      <CommandPalette />

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-[72px]" : "w-[248px]"} shrink-0 relative flex-col sticky top-0 h-screen transition-[width] duration-200`}
        style={sidebarStyle}
      >
        {/* Signature coral radial glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: -60, right: -60, width: 220, height: 220, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(244,63,94,0.18) 0%, rgba(99,102,241,0.06) 55%, transparent 75%)",
            filter: "blur(40px)",
          }}
        />
        <SidebarBody
          user={user}
          onLogout={handleLogout}
          onNavigate={() => {}}
          collapsed={collapsed}
          theme={theme}
          onToggleTheme={toggleTheme}
          focus={focus}
          onToggleFocus={toggleFocus}
        />
        {/* Collapse toggle */}
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-6 w-6 h-6 rounded-full flex items-center justify-center bg-surface hover:bg-surface-2 transition-colors"
          style={{
            border: "1px solid var(--cf-hairline-strong)",
            boxShadow: "var(--cf-shadow-sm)",
            color: "var(--cf-ink)",
          }}
        >
          {collapsed ? (
            <ChevronsRight size={12} strokeWidth={2.5} />
          ) : (
            <ChevronsLeft size={12} strokeWidth={2.5} />
          )}
        </button>
      </aside>

      {/* Mobile top bar — matches the dark sidebar drawer aesthetic so the
          logo treatment is consistent across both mobile navigation surfaces. */}
      <div
        className="md:hidden fixed top-0 inset-x-0 z-30 h-14 flex items-center justify-between px-4"
        style={{ background: "linear-gradient(180deg,#0A0C13 0%, #10131C 100%)" }}
      >
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-btn"
          className="cf-touch p-2 -ml-2 rounded-lg text-white"
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={2} />
        </button>
        <Link to="/" className="inline-flex items-center">
          <CashflowLockup size={26} variant="light" />
        </Link>
        <button
          data-testid="mobile-cmdk-btn"
          onClick={openCmdK}
          className="cf-touch p-2 -mr-2 rounded-lg text-white"
          aria-label="Open command palette"
        >
          <Command size={18} strokeWidth={2} />
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0" style={{ background: "rgba(6,8,16,0.6)" }} onClick={() => setDrawerOpen(false)} />
          <div
            className="relative w-[280px] max-w-[85vw] h-full flex flex-col"
            style={{ ...sidebarStyle, animation: "cf-fade-up 0.24s ease-out both" }}
          >
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-3 right-3 cf-touch p-2 rounded-lg text-white/70 hover:text-white"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            <SidebarBody
              user={user}
              onLogout={handleLogout}
              onNavigate={() => setDrawerOpen(false)}
              collapsed={false}
              mobile
              theme={theme}
              onToggleTheme={toggleTheme}
              focus={focus}
              onToggleFocus={toggleFocus}
            />
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-x-clip pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}

import ProjectFilterPill from "@/components/ProjectFilterPill";
import { monthLabel as _monthLabel } from "@/lib/format";

/**
 * Big, prominent selected-range indicator that sits directly under the page
 * title. Renders coral-outlined, monospace-numeric, unmissable — the finance
 * team should never wonder which window is currently active.
 */
function RangeBanner({ months, horizonLabel }) {
  if (!months?.length) return null;
  const from = _monthLabel(months[0]);
  const to = _monthLabel(months[months.length - 1]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        data-testid="page-range-banner"
        className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 rounded-full num"
        style={{
          background: "var(--cf-accent-solid)",
          color: "var(--cf-accent-deep)",
          border: "1px solid var(--cf-accent-2)",
          fontSize: "13px",
          fontWeight: 700,
          letterSpacing: "0.02em",
          boxShadow: "0 1px 2px rgba(244,63,94,0.15)",
        }}
      >
        <span>{from}</span>
        <span style={{ opacity: 0.55, fontSize: "12px" }}>→</span>
        <span>{to}</span>
        <span style={{ opacity: 0.55, fontSize: "11px", marginLeft: 4 }}>
          · {horizonLabel || `${months.length}m`}
        </span>
      </div>
    </div>
  );
}

export function PageShell({ title, kicker, actions, children, testId, hideProjectFilter = false, rangeMonths, rangeLabel }) {
  return (
    <div data-testid={testId} className="mx-auto max-w-[1440px] px-4 md:px-10 py-6 md:py-10 space-y-6 md:space-y-8">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6">
        <div>
          {kicker && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-light font-semibold mb-2">
              {kicker}
            </div>
          )}
          <h1
            className="font-display text-ink"
            style={{ fontWeight: 800, fontSize: "clamp(26px, 4vw, 34px)", letterSpacing: "-0.028em", lineHeight: 1.05 }}
          >
            {title}
          </h1>
          {rangeMonths && (
            <div className="mt-3">
              <RangeBanner months={rangeMonths} horizonLabel={rangeLabel} />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {!hideProjectFilter && <ProjectFilterPill />}
          {actions}
        </div>
      </header>
      {children}
    </div>
  );
}
